import type React from "react";
import { useEffect, useState } from "react";
import { Box, MenuItem, Typography, InputAdornment } from "@mui/material";
import CustomTextField from "../../../../../components/ui/Textfield";
import CustomSelect from "../../../../../components/ui/Select";
import PhotoEditor from "../../../../../components/PhotoEditor";
import apiService from "../../../../../service/Api";
import { toast } from "react-toastify";
import type { ICompany } from "../../../../../types/package";
import { PACKAGE_TYPES, type PackageBuilderState } from "../types";

interface Props {
  state: PackageBuilderState;
  update: (patch: Partial<PackageBuilderState>) => void;
}

const GeneralTab: React.FC<Props> = ({ state, update }) => {
  const [funeralHomes, setFuneralHomes] = useState<ICompany[]>([]);
  const [loadingHomes, setLoadingHomes] = useState(false);

  useEffect(() => {
    if (state.package_type !== "FuneralHome") return;
    let cancelled = false;
    setLoadingHomes(true);
    apiService
      .get<{ results: ICompany[] }>(
        "api/companies/company/?company_type=funeral_home&page_size=200",
      )
      .then((res) => {
        if (!cancelled) setFuneralHomes(res.data.results || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load funeral homes.");
      })
      .finally(() => {
        if (!cancelled) setLoadingHomes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.package_type]);

  const handleSaveImage = async (imageData: string) => {
    try {
      update({ logo_preview: imageData });
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `package_logo_${Date.now()}.jpg`, {
        type: blob.type,
      });
      update({ logo_file: file, logo_preview: imageData });
    } catch {
      toast.error("Failed to process image. Please try again.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 760 }}>
      <Field label="Package Name">
        <CustomTextField
          placeholder="e.g. Muzukuru Pro"
          value={state.name}
          onChange={(e) => update({ name: e.target.value })}
        />
      </Field>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Field label="Category">
            <CustomSelect
              label=""
              value={state.package_type}
              onChange={(e) =>
                update({ package_type: e.target.value as string })
              }
            >
              {PACKAGE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </CustomSelect>
          </Field>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Field label="Price (USD)">
            <CustomTextField
              type="number"
              placeholder="0"
              value={state.base_price}
              onChange={(e) => update({ base_price: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Field>
        </Box>
      </Box>

      {state.package_type === "FuneralHome" && (
        <Field label="Funeral Home">
          <CustomSelect
            label=""
            value={state.funeral_home_id}
            onChange={(e) =>
              update({ funeral_home_id: e.target.value as string })
            }
            disabled={loadingHomes}
          >
            {funeralHomes.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Field>
      )}

      <Field label="Description">
        <CustomTextField
          multiline
          minRows={4}
          placeholder="Describe what this package includes..."
          value={state.description}
          onChange={(e) => update({ description: e.target.value })}
        />
      </Field>

      <Field label="Package Logo">
        <PhotoEditor
          initialImage={state.logo_preview}
          onSave={handleSaveImage}
          title="Upload Package Logo"
        />
      </Field>
    </Box>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        mb: 0.75,
        color: "text.secondary",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

export default GeneralTab;
